import { msg } from '@lingui/core/macro';
import { errorMessage } from './i18n/errorMessages';
import {
  bindMessageCacheAccount,
  closeMessageCacheAccount,
  captureMessageCachePurge,
} from './lib/message-cache-session';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { Trans, useLingui } from '@lingui/react/macro';
import { microsoftOIDCErrorMessage, sessionEndedMessage } from './i18n/authMessages';
import { Navigate, RouterProvider, useParams, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { createIntentionalLogoutState } from './context/AppContext';
import { ToastProvider } from './components/Toast';
import { NavigationProvider } from './components/Navigation/NavigationContext';
import AntdProvider from './components/AntdProvider';
import Sidebar from './components/Sidebar';
import PrimaryNavigationRail from './components/PrimaryNavigationRail';
import ServerSidebar from './components/ServerSidebar';
import ShellLayout from './components/ShellLayout';
import ChannelView from './components/ChannelView';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import AgentManager, { CreateAgentModal } from './components/AgentManager';
import AgentSidebar from './components/AgentSidebar';
import AgentDetailView from './components/AgentDetailView';
import ComputerSidebar from './components/ComputerSidebar';
import ComputerDetailView from './components/ComputerDetailView';
import ServerSettingsPage from './components/ServerSettings/ServerSettingsPage';
import PersonalSettingsModal from './components/PersonalSettingsModal';
import InvitationsInbox from './components/InvitationsInbox';
import ThreadPanel from './components/ThreadPanel';
import PersonalOverview from './components/PersonalOverview';
import TasksPanel from './components/TasksPanel';
import ServerTasksPage from './components/ServerTasksPage';
import PersonalTasksPage from './components/PersonalTasksPage';
import InsightsPanel from './components/InsightsPanel';
import ProjectMembersPage from './components/ProjectMembersPage';
import { useWebSocket } from './hooks/useWebSocket';
import { useMobileViewport } from './hooks/useMobileViewport';
import { useRemoteNodes } from './hooks/useRemoteNodes';
import { discardAccountDrafts } from './hooks/useComposerDraft';
import { useThreadRoute } from './hooks/useThreadRoute';
import { useSidebarCollapsed } from './hooks/useSidebarCollapsed';
import { useServerTransitionController } from './hooks/useServerTransitionController';
import {
  fetchHumanSessionStatus,
  fetchMe,
  fetchGuildMembers,
  ApiError,
  type Agent,
} from './lib/api';
import { runUnsavedGuards } from './hooks/useUnsavedChangesGuard';
import { rememberLastChannelId, resolveDefaultChannel } from './lib/defaultChannel';
import {
  loadLastServerId,
  rememberLastServerId,
  resolveServerSelection,
} from './lib/serverSelection';
import { isComputersRoute, routes } from './lib/routes';
import { router } from './lib/router';
import {
  useAgentStore,
  useAgentActivityStore,
  useAuthStore,
  useChannelStore,
  useMessageStore,
  useDmStore,
  usePresenceStore,
  useTypingStore,
  useWsStore,
  useUIActionStore,
  useServerStore,
  resetAllStores,
  resetServerScopedStores,
} from './stores';
import type { CreateServerReceipt } from './stores/useServerStore';
import { commitServerChannelSnapshot } from './stores/useChannelStore';
import './index.css';
import AgentDetailPanel from './components/AgentDetailPanel';
import DirectMessagePanel from './components/DirectMessagePanel';
import 'highlight.js/styles/github.css';

const AUTH_READY_TIMEOUT_MS = 500;
const AUTH_READY_POLL_MS = 50;

async function waitForAuthReady(): Promise<void> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < AUTH_READY_TIMEOUT_MS) {
    try {
      await fetchMe();
      return;
    } catch (err) {
      if (err instanceof ApiError && err.status !== 401) return;
    }

    await new Promise((resolve) => setTimeout(resolve, AUTH_READY_POLL_MS));
  }
}

type RouteView =
  | 'channel'
  | 'dm'
  | 'dm-list'
  | 'agents'
  | 'tasks'
  | 'overview'
  | 'my-tasks'
  | 'insights'
  | 'members'
  | 'invitations'
  | 'remote-nodes'
  | 'server-settings'
  | 'redirect';

function useRouteView(): {
  view: RouteView;
  channelId?: string;
  dmChannelId?: string;
  guildId?: string;
  agentId?: string;
  computerId?: string;
  threadId?: string;
  memberId?: string;
} {
  const params = useParams<{
    guildId?: string;
    channelId?: string;
    dmChannelId?: string;
    agentId?: string;
    computerId?: string;
    threadId?: string;
    memberId?: string;
  }>();
  const location = useLocation();
  const path = location.pathname;

  if (path === '/me' || path === '/me/') return { view: 'overview' };
  if (path === '/me/tasks') {
    return { view: 'my-tasks' };
  }
  if (path.startsWith('/me/agents')) {
    return { view: 'agents', agentId: params.agentId };
  }
  if (isComputersRoute(path)) {
    return { view: 'remote-nodes', computerId: params.computerId };
  }
  if (params.dmChannelId) {
    return { view: 'dm', dmChannelId: params.dmChannelId };
  }
  if (path === '/dm' || path === '/dm/') {
    return { view: 'dm-list' };
  }
  if (params.guildId) {
    if (path.endsWith('/invitations')) return { view: 'invitations', guildId: params.guildId };
    if (path.endsWith('/tasks')) return { view: 'tasks', guildId: params.guildId };
    if (path.endsWith('/insights')) return { view: 'insights', guildId: params.guildId };
    if (path.match(/\/members\/[^/]+$/))
      return { view: 'members', guildId: params.guildId, memberId: params.memberId };
    if (path.endsWith('/members')) return { view: 'members', guildId: params.guildId };
    if (path.match(/\/settings(?:\/.*)?$/))
      return { view: 'server-settings', guildId: params.guildId };
    if (params.channelId)
      return {
        view: 'channel',
        channelId: params.channelId,
        guildId: params.guildId,
        threadId: params.threadId,
      };
    return { view: 'redirect', guildId: params.guildId };
  }
  return { view: 'redirect' };
}

function AppInner() {
  const { i18n } = useLingui();
  // Zustand stores
  const currentUser = useAuthStore((s) => s.currentUser);
  const permissions = useAuthStore((s) => s.permissions);
  const guildId = useAuthStore((s) => s.guildId);
  const initialized = useAuthStore((s) => s.initialized);
  const setGuildId = useAuthStore((s) => s.setGuildId);
  const setInitialized = useAuthStore((s) => s.setInitialized);
  const loadCurrentUser = useAuthStore((s) => s.loadCurrentUser);
  const loadPermissions = useAuthStore((s) => s.loadPermissions);

  const channels = useChannelStore((s) => s.channels);
  const currentChannelId = useChannelStore((s) => s.currentChannelId);
  const loadChannels = useChannelStore((s) => s.loadChannels);
  const selectChannel = useChannelStore((s) => s.selectChannel);
  const selectedReadAccess = useRef<{
    channelId: string;
    accountId: string | null;
    guildId: string | null;
    readable: boolean;
  } | null>(null);

  const dmChannels = useDmStore((s) => s.dmChannels);
  const loadDmChannels = useDmStore((s) => s.loadDmChannels);
  const openDm = useDmStore((s) => s.openDm);

  const guildMembers = useServerStore((s) => s.members);
  const guilds = useServerStore((s) => s.guilds);
  const guildsLoaded = useServerStore((s) => s.guildsLoaded);
  const bindServerAccount = useServerStore((s) => s.bindAccount);
  const refreshGuilds = useServerStore((s) => s.refreshGuilds);

  const loadOnlineUsers = usePresenceStore((s) => s.loadOnlineUsers);

  const clearExpiredTyping = useTypingStore((s) => s.clearExpiredTyping);
  const clearExpiredBackgroundRuns = useAgentActivityStore((s) => s.clearExpiredBackgroundRuns);

  const setSendWsMessage = useWsStore((s) => s.setSendWsMessage);
  const setRegisterAckTimer = useWsStore((s) => s.setRegisterAckTimer);

  const navigate = useNavigate();
  const shellLocation = useLocation();
  const routeView = useRouteView();
  // A Task Thread is a detail panel over the current Server surface, not a
  // destination of its own. Keep it in the query for every Server surface so
  // a task selected from the rail does not replace Members, Insights, or a
  // Channel tab with that task's parent Channel.
  const serverPanelThreadId = routeView.guildId
    ? new URLSearchParams(shellLocation.search).get('thread') || undefined
    : undefined;
  const serverTasksThreadId = routeView.view === 'tasks' ? serverPanelThreadId : undefined;
  const myTasksThreadId =
    routeView.view === 'my-tasks'
      ? new URLSearchParams(shellLocation.search).get('thread') || undefined
      : undefined;
  const overviewThreadId =
    routeView.view === 'overview'
      ? new URLSearchParams(shellLocation.search).get('thread') || undefined
      : undefined;
  const overviewDmChannelId =
    routeView.view === 'overview'
      ? new URLSearchParams(shellLocation.search).get('dm') || undefined
      : undefined;
  const overviewAgentId =
    routeView.view === 'overview'
      ? new URLSearchParams(shellLocation.search).get('agent') || undefined
      : undefined;
  // A DM panel is not a Channel tab surface. Older cold loads could append
  // `tab=chat` before the DM roster hydrated; normalize that stale query once.
  useEffect(() => {
    if (routeView.view !== 'overview' || !overviewDmChannelId) return;
    const search = new URLSearchParams(shellLocation.search);
    if (!search.has('tab')) return;
    search.delete('tab');
    navigate({ pathname: routes.overview(), search: `?${search.toString()}` }, { replace: true });
  }, [routeView.view, overviewDmChannelId, shellLocation.search, navigate]);
  const isMobile = useMobileViewport();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showPersonalSettings, setShowPersonalSettings] = useState(false);
  const settingsRestoreFocus = useRef<() => void>(() => {});
  const openPersonalSettings = useCallback((restoreFocus: () => void) => {
    const navigationButton = isMobile
      ? document.querySelector<HTMLButtonElement>('button[aria-controls="shell-rail-drawer"]')
      : null;
    settingsRestoreFocus.current = navigationButton ? () => navigationButton.focus() : restoreFocus;
    setDrawerOpen(false);
    setShowPersonalSettings(true);
  }, [isMobile]);
  useEffect(() => { setShowPersonalSettings(false); }, [currentUser?.id]);
  const { collapsed: sidebarCollapsed, toggle: toggleSidebarCollapsed } = useSidebarCollapsed();
  const agentsList = useAgentStore((s) => s.agents);
  const loadAgents = useAgentStore((s) => s.loadAgents);
  const resetAgents = useAgentStore((s) => s.reset);
  const [authChecked, setAuthChecked] = useState(false);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [memberDmChannelId, setMemberDmChannelId] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [sessionError, setSessionError] = useState<MessageDescriptor | null>(null);
  const [showRegister, setShowRegister] = useState(
    () => new URLSearchParams(window.location.search).get('auth_surface') === 'register',
  );
  const [oidcError, setOIDCError] = useState(() =>
    microsoftOIDCErrorMessage(new URLSearchParams(window.location.search).get('oidc_error')),
  );
  const oidcErrorRef = useRef<HTMLDivElement>(null);
  const intentionalLogout = useRef(createIntentionalLogoutState());
  // Only an interactive login or registration sets this. It is intentionally
  // in-memory and consumed during bootstrap, so refreshes and later deep links
  // retain the non-disclosing not-found behavior.
  const postAuthenticationFallbackRef = useRef(false);
  const routeGuildIdRef = useRef(routeView.guildId);
  routeGuildIdRef.current = routeView.guildId;
  // Bootstrap belongs to the authenticated session, not to a route location.
  // React Router may replace the navigate function as locations change; keep
  // the latest callback available without turning each navigation into a full
  // re-bootstrap that clears and reloads the active Server stores.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const subscribedServerChannelsRef = useRef<Set<string>>(new Set());
  const computers = useRemoteNodes(
    authenticated && initialized && routeView.view === 'remote-nodes',
  );
  const authRecoveryGeneration = useRef(0);
  const clearAuthenticatedState = useCallback(() => {
    authRecoveryGeneration.current++;
    closeMessageCacheAccount();
    setAuthenticated(false);
    setShowPersonalSettings(false);
    resetAllStores();
    resetAgents();
    computers.reset();
    setShowCreateAgent(false);
    setMemberDmChannelId(null);
  }, [resetAgents, computers.reset]);
  const handleAuthClose = useCallback(() => {
    setSessionError(
      intentionalLogout.current.shouldAnnounceSessionEnded() ? sessionEndedMessage : null,
    );
    clearAuthenticatedState();
  }, [clearAuthenticatedState]);
  const viewerRecovery = useRef(false);
  const [identityRevision, setIdentityRevision] = useState(0);
  useEffect(() => {
    viewerRecovery.current = false;
  }, [shellLocation.key]);
  useEffect(() => {
    let disposed = false;
    const recover = () => {
      clearAuthenticatedState();
      if (viewerRecovery.current) {
        setSessionError(sessionEndedMessage);
        return;
      }
      viewerRecovery.current = true;
      const recoveryGeneration = authRecoveryGeneration.current;
      void fetchHumanSessionStatus()
        .then((status) => {
          if (disposed || recoveryGeneration !== authRecoveryGeneration.current) return;
          setAuthenticated(status.authenticated);
          setAuthChecked(true);
          setIdentityRevision((revision) => revision + 1);
        })
        .catch(() => {
          if (disposed || recoveryGeneration !== authRecoveryGeneration.current) return;
          setSessionError(sessionEndedMessage);
          setAuthChecked(true);
        });
    };
    window.addEventListener('message-viewer-mismatch', recover);
    window.addEventListener('message-auth-ended', handleAuthClose);
    return () => {
      disposed = true;
      window.removeEventListener('message-viewer-mismatch', recover);
      window.removeEventListener('message-auth-ended', handleAuthClose);
    };
  }, [clearAuthenticatedState, handleAuthClose]);
  const activeServerChannelId =
    routeView.view === 'channel' && routeView.guildId === guildId
      ? (routeView.channelId ?? null)
      : null;
  const activePublicChannelId =
    channels.find(
      (channel) =>
        channel.id === activeServerChannelId &&
        channel.channel_type === 0 &&
        channel.visibility === 'public',
    )?.id ?? null;
  const {
    subscribe,
    unsubscribe,
    unsubscribeServerChannels = () => {},
    sendWsMessage,
    registerAckTimer,
  } = useWebSocket(authenticated, handleAuthClose, activeServerChannelId);
  const clearServerSubscriptionsRef = useRef(unsubscribeServerChannels);
  clearServerSubscriptionsRef.current = unsubscribeServerChannels;
  const clearServerSubscriptions = useCallback(() => clearServerSubscriptionsRef.current(), []);
  const resetCommittedServerUI = useCallback(() => {
    setMemberDmChannelId(null);
  }, []);
  const {
    transition: serverTransition,
    inaccessibleServer,
    fallbackGuildId: serverFallbackGuildId,
    retry: retryServerTransition,
    setBootstrapInaccessible,
    markRemovedMembershipFallback,
  } = useServerTransitionController({
    authenticated,
    initialized,
    route: { guildId: routeView.guildId, view: routeView.view },
    clearServerSubscriptions,
    onCommitServerContext: resetCommittedServerUI,
  });

  useEffect(() => {
    setSendWsMessage(sendWsMessage);
    setRegisterAckTimer(registerAckTimer);
  }, [sendWsMessage, setSendWsMessage, registerAckTimer, setRegisterAckTimer]);

  // The rail drawer is a transient overlay over the main surface, so it must
  // never survive the thing it was opened to change: any navigation, or growing
  // past the breakpoint into the docked rail, dismisses it. Keyed on the history
  // entry rather than the pathname so that re-selecting the channel the user is
  // already in still closes it.
  useEffect(() => {
    setDrawerOpen(false);
  }, [shellLocation.key, isMobile]);

  useEffect(() => {
    if (oidcError) oidcErrorRef.current?.focus();
  }, [oidcError, authChecked, authenticated, initialized]);

  // The agent detail surface resolves its agent out of the owner's agent list,
  // so the shell loads that list for the route itself. Leaving the load to
  // `AgentSidebar` would tie the detail surface to a sidebar that the member can
  // collapse away.
  useEffect(() => {
    if (
      authenticated &&
      initialized &&
      (routeView.view === 'agents' || (routeView.view === 'overview' && overviewAgentId))
    )
      void loadAgents();
  }, [authenticated, initialized, routeView.view, overviewAgentId, loadAgents]);

  // My Agents is a detail-oriented personal surface. Its root route therefore
  // selects the first owned agent once the server-owned list arrives instead
  // of leaving the content pane in a dead-end empty state.
  useEffect(() => {
    if (
      !authenticated ||
      !initialized ||
      routeView.view !== 'agents' ||
      routeView.agentId ||
      agentsList.length === 0
    )
      return;
    navigate(routes.agent(agentsList[0].id), { replace: true });
  }, [authenticated, initialized, routeView.view, routeView.agentId, agentsList, navigate]);

  useEffect(() => {
    fetchHumanSessionStatus()
      .then((status) => {
        setAuthenticated(status.authenticated);
        setAuthChecked(true);
      })
      .catch(() => {
        setAuthenticated(false);
        setAuthChecked(true);
      });
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    const controller = new AbortController();

    const init = async () => {
      try {
        await loadCurrentUser();
        const userId = useAuthStore.getState().currentUser?.id;
        if (!userId) throw new Error('Authenticated session has no account.');
        bindMessageCacheAccount(userId);
        bindServerAccount(userId);
        await loadPermissions();
        // Resolve routes only against the membership list. Unknown ids are
        // never fetched individually, preserving the non-disclosure boundary
        // for inaccessible deep links.
        try {
          const pendingRecovery = useServerStore.getState().createRecovery;
          let recoveredReceipt: CreateServerReceipt | null = null;
          if (pendingRecovery) {
            try {
              recoveredReceipt = await useServerStore.getState().createServer();
            } catch {
              // Recovery errors stay durable and visible, but must not prevent
              // the existing membership list from bootstrapping normally.
            }
          }
          const refresh = recoveredReceipt
            ? { status: 'applied' as const, guilds: useServerStore.getState().guilds }
            : await refreshGuilds();
          if (refresh.status !== 'applied') return;
          const availableGuilds = refresh.guilds;
          const rememberedGuildId = loadLastServerId(userId);
          const targetGuildId = recoveredReceipt?.guildId ?? routeGuildIdRef.current;
          if (recoveredReceipt) {
            navigateRef.current(
              routes.channel(recoveredReceipt.guildId, recoveredReceipt.landingChannelId),
              { replace: true },
            );
          }
          const postAuthenticationFallbackGuildId = postAuthenticationFallbackRef.current
            ? availableGuilds.find((guild) => guild.owner_id === userId)?.id
            : undefined;
          const selection = resolveServerSelection(
            availableGuilds,
            targetGuildId,
            rememberedGuildId,
            { postAuthenticationFallbackGuildId },
          );
          postAuthenticationFallbackRef.current = false;
          if (selection.kind === 'inaccessible') {
            clearServerSubscriptionsRef.current();
            setGuildId(null);
            setBootstrapInaccessible(true);
            resetServerScopedStores();
          } else if (selection.kind !== 'empty') {
            // Bootstrap uses the same staged-then-commit rule as an in-app
            // Server switch. Publishing guildId before both reads settle can
            // expose a half-installed context when one request succeeds and the
            // other fails.
            await commitServerChannelSnapshot(
              selection.guildId,
              (signal) => fetchGuildMembers(selection.guildId, signal),
              (channelResult, members) => {
                if (cancelled || useAuthStore.getState().currentUser?.id !== userId) return;
                clearServerSubscriptionsRef.current();
                resetServerScopedStores();
                useChannelStore.getState().setSnapshot(channelResult, selection.guildId);
                useServerStore.getState().replaceMembers(selection.guildId, members);
                setGuildId(selection.guildId);
                rememberLastServerId(userId, selection.guildId);
                if (selection.kind === 'post-authentication-fallback') {
                  // The requested Server was never queried. Route only to a
                  // membership-list-derived Server after its own channels have
                  // been staged, preferring its welcome channel.
                  const landing = resolveDefaultChannel(
                    channelResult.channels,
                    selection.guildId,
                    'onboarding',
                  );
                  navigateRef.current(
                    landing
                      ? routes.channel(selection.guildId, landing.id)
                      : routes.server(selection.guildId),
                    { replace: true },
                  );
                } else if (selection.kind === 'removed-fallback') {
                  markRemovedMembershipFallback(selection.guildId);
                }
              },
              controller.signal,
            );
          }
        } catch {
          if (useServerStore.getState().createRecovery?.kind === 'receipt') {
            useServerStore
              .getState()
              .setCreateError(
                msg`The Project was created, but its data could not be loaded. Reload to retry.`,
              );
          }
        }
        // DM deep links are validated against this list once initialization finishes.
        await Promise.all([loadOnlineUsers(), loadDmChannels()]);
      } finally {
        if (!cancelled) {
          setInitialized();
          if (typeof performance.mark === 'function') {
            performance.clearMarks('message-cache:bootstrap-complete');
            performance.mark('message-cache:bootstrap-complete');
          }
        }
      }
    };
    init();

    const interval = setInterval(() => {
      loadOnlineUsers();
    }, 30_000);
    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
    };
  }, [
    authenticated,
    identityRevision,
    loadCurrentUser,
    loadPermissions,
    setGuildId,
    bindServerAccount,
    refreshGuilds,
    loadChannels,
    loadDmChannels,
    loadOnlineUsers,
    setInitialized,
    setBootstrapInaccessible,
    markRemovedMembershipFallback,
  ]);

  // Transient activity cleanup interval
  useEffect(() => {
    const interval = setInterval(() => {
      clearExpiredTyping();
      clearExpiredBackgroundRuns();
    }, 1000);
    return () => clearInterval(interval);
  }, [clearExpiredBackgroundRuns, clearExpiredTyping]);

  // Sync URL channel/DM param into store state, remember the channel the user
  // is actually reading so a later entry at `/` returns to it, and redirect
  // away from stale URLs (channel left/deleted).
  useEffect(() => {
    if (!initialized) return;
    if (routeView.guildId && routeView.guildId !== guildId) return;
    if (
      (routeView.guildId && serverTransition.status !== 'idle') ||
      (routeView.guildId && routeView.guildId !== guildId) ||
      inaccessibleServer
    )
      return;
    const targetId =
      routeView.view === 'channel'
        ? routeView.channelId
        : routeView.view === 'dm'
          ? routeView.dmChannelId
          : undefined;
    if (!targetId) return;

    const isKnownChannel = routeView.view === 'channel' && channels.some((c) => c.id === targetId);
    const isKnownDm = routeView.view === 'dm' && dmChannels.some((d) => d.id === targetId);

    if (routeView.view === 'channel' && !isKnownChannel) {
      const fallback = resolveDefaultChannel(channels, guildId, 'main');
      if (fallback) {
        navigate(routes.channel(guildId!, fallback.id), { replace: true });
      }
      return;
    }

    if (routeView.view === 'dm' && !isKnownDm) {
      navigate(routes.root(), { replace: true });
      return;
    }

    if (isKnownChannel && routeView.guildId) {
      rememberLastChannelId(routeView.guildId, targetId);
    }

    const readable =
      isKnownDm ||
      channels.some(
        (channel) =>
          channel.id === targetId && channel.channel_type === 0 && channel.is_member === true,
      );
    const previousAccess = selectedReadAccess.current;
    const gainedMembership =
      readable &&
      previousAccess?.channelId === targetId &&
      previousAccess.accountId === (currentUser?.id ?? null) &&
      previousAccess.guildId === guildId &&
      !previousAccess.readable;
    selectedReadAccess.current = {
      channelId: targetId,
      accountId: currentUser?.id ?? null,
      guildId,
      readable,
    };
    if (targetId !== currentChannelId || gainedMembership) {
      selectChannel(targetId);
    }
  }, [
    initialized,
    channels,
    dmChannels,
    routeView.view,
    routeView.channelId,
    routeView.dmChannelId,
    routeView.guildId,
    currentChannelId,
    currentUser?.id,
    selectChannel,
    navigate,
    guildId,
    serverTransition.status,
    inaccessibleServer,
  ]);

  // The authenticated root is the Personal Overview. Server routes that omit
  // a Channel retain their existing default-channel resolution below.
  useEffect(() => {
    if (!initialized || routeView.view !== 'redirect') return;
    if (!routeView.guildId) {
      navigate(routes.overview(), { replace: true });
      return;
    }
    if (routeView.guildId !== guildId) return;
    if (
      (routeView.guildId && serverTransition.status !== 'idle') ||
      (routeView.guildId && routeView.guildId !== guildId) ||
      inaccessibleServer
    )
      return;
    const target = resolveDefaultChannel(channels, guildId, 'onboarding');
    if (target) {
      navigate(routes.channel(guildId!, target.id), { replace: true });
    }
  }, [
    initialized,
    channels,
    routeView.view,
    routeView.guildId,
    navigate,
    guildId,
    serverTransition.status,
    inaccessibleServer,
  ]);

  useEffect(() => {
    return useUIActionStore.subscribe((state) => {
      const action = state.pendingAction;
      if (!action) return;
      useUIActionStore.getState().consume();
      if (action.type === 'open_agent_manager') {
        if (guildId) {
          navigate(routes.members(guildId));
        }
        setShowCreateAgent(true);
      }
    });
  }, [navigate, guildId]);

  useEffect(() => {
    if (
      routeView.view !== 'members' ||
      !routeView.memberId ||
      (routeView.guildId && serverTransition.status !== 'idle') ||
      (routeView.guildId && routeView.guildId !== guildId) ||
      !guildMembers.some((member) => member.user_id === routeView.memberId)
    ) {
      setMemberDmChannelId(null);
      return;
    }
    let cancelled = false;
    openDm(routeView.memberId)
      .then((dm) => {
        if (!cancelled) setMemberDmChannelId(dm.id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    routeView.view,
    routeView.memberId,
    routeView.guildId,
    guildId,
    guildMembers,
    serverTransition.status,
    openDm,
  ]);

  useEffect(() => {
    if (
      !initialized ||
      (routeView.guildId && serverTransition.status !== 'idle') ||
      (routeView.guildId && routeView.guildId !== guildId) ||
      inaccessibleServer
    )
      return;
    const next = new Set(
      channels
        .filter((channel) => channel.channel_type === 0 && channel.is_member === true)
        .map((channel) => channel.id),
    );
    // Discovery grants history access to the public conversation being viewed, not membership.
    if (activePublicChannelId) next.add(activePublicChannelId);
    for (const channelId of subscribedServerChannelsRef.current) {
      if (!next.has(channelId)) unsubscribe(channelId);
    }
    for (const channelId of next) {
      if (!subscribedServerChannelsRef.current.has(channelId)) subscribe(channelId);
    }
    subscribedServerChannelsRef.current = next;
  }, [
    initialized,
    channels,
    subscribe,
    unsubscribe,
    serverTransition.status,
    routeView.guildId,
    activePublicChannelId,
    guildId,
    inaccessibleServer,
  ]);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen((o) => !o);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const routeGuildAccessible =
    !routeView.guildId || guilds.some((entry) => entry.id === routeView.guildId);
  const serverContextMismatch = !!routeView.guildId && routeView.guildId !== guildId;
  const serverTransitionPending = !!routeView.guildId && serverTransition.status !== 'idle';
  const serverRouteReady =
    initialized &&
    guildsLoaded &&
    routeGuildAccessible &&
    !serverContextMismatch &&
    !serverTransitionPending &&
    !inaccessibleServer;

  const threadRoute = useThreadRoute({
    guildId: serverRouteReady ? routeView.guildId : undefined,
    channelId: serverRouteReady ? routeView.channelId : undefined,
    threadId: serverRouteReady ? routeView.threadId : undefined,
    subscribe,
  });

  // A query-backed Task Thread needs the same realtime stream as a route-native
  // thread, regardless of which Server surface it overlays.
  useEffect(() => {
    if (serverRouteReady && serverPanelThreadId) subscribe(serverPanelThreadId);
  }, [serverRouteReady, serverPanelThreadId, subscribe]);

  // Personal inbox threads are authorized through the Task read model, not an
  // active Server context. Keep the selected one live without navigating away
  // from /me/tasks.
  useEffect(() => {
    if (myTasksThreadId) subscribe(myTasksThreadId);
  }, [myTasksThreadId, subscribe]);

  // The Task thread we pushed over Server Tasks. A cold link has no entry of
  // ours underneath it, and task-to-task replacement inherits that distinction.
  const pushedServerTasksThreadId = useRef<string | null>(null);

  const openServerTaskThread = useCallback(
    (threadId: string) => {
      if (!routeView.guildId) return;
      const replacesThread = !!serverPanelThreadId;
      const search = new URLSearchParams(shellLocation.search);
      search.set('thread', threadId);
      if (replacesThread) {
        pushedServerTasksThreadId.current =
          pushedServerTasksThreadId.current === serverPanelThreadId ? threadId : null;
      } else {
        pushedServerTasksThreadId.current = threadId;
      }
      navigate(
        { pathname: shellLocation.pathname, search: `?${search.toString()}` },
        {
          // A task-to-task click replaces the thread in the pane. Keeping every
          // selection in history would make Back walk task cards rather than leave
          // the Server surface it was opened over.
          replace: replacesThread,
        },
      );
    },
    [
      navigate,
      routeView.guildId,
      shellLocation.pathname,
      shellLocation.search,
      serverPanelThreadId,
    ],
  );

  const closeServerTaskThread = useCallback(() => {
    const search = new URLSearchParams(shellLocation.search);
    search.delete('thread');
    navigate({
      pathname: shellLocation.pathname,
      search: search.size ? `?${search.toString()}` : '',
    });
  }, [navigate, shellLocation.pathname, shellLocation.search]);

  const dismissServerTaskThread = useCallback(() => {
    if (!routeView.guildId || !serverPanelThreadId) return;
    if (pushedServerTasksThreadId.current === serverPanelThreadId) {
      // Give the pushed entry up before traversing so repeated outside presses
      // cannot traverse past the Server surface.
      pushedServerTasksThreadId.current = null;
      navigate(-1);
      return;
    }
    const search = new URLSearchParams(shellLocation.search);
    search.delete('thread');
    navigate(
      { pathname: shellLocation.pathname, search: search.size ? `?${search.toString()}` : '' },
      { replace: true },
    );
  }, [
    navigate,
    routeView.guildId,
    shellLocation.pathname,
    shellLocation.search,
    serverPanelThreadId,
  ]);

  const openMyTaskThread = useCallback(
    (threadId: string) => {
      navigate(routes.myTasksThread(threadId), { replace: !!myTasksThreadId });
    },
    [navigate, myTasksThreadId],
  );

  const closeMyTaskThread = useCallback(() => {
    navigate(routes.myTasks());
  }, [navigate]);

  const openOverviewTaskThread = useCallback(
    (threadId: string) => {
      navigate(`${routes.overview()}?thread=${encodeURIComponent(threadId)}`, {
        replace: !!overviewThreadId,
      });
    },
    [navigate, overviewThreadId],
  );

  const openOverviewAgentPanel = useCallback(
    (agentId: string) => {
      navigate(`${routes.overview()}?agent=${encodeURIComponent(agentId)}`, {
        replace: !!overviewAgentId,
      });
    },
    [navigate, overviewAgentId],
  );

  const closeOverviewPanel = useCallback(() => {
    navigate(routes.overview());
  }, [navigate]);

  const handleLogin = useCallback(async () => {
    authRecoveryGeneration.current++;
    viewerRecovery.current = false;
    postAuthenticationFallbackRef.current = true;
    await waitForAuthReady();
    intentionalLogout.current.cancel();
    setSessionError(null);
    setAuthenticated(true);
    if (new URLSearchParams(window.location.search).get('return_to') === '/invite') {
      window.location.assign('/invite');
    }
  }, []);

  const logoutAuthGeneration = authRecoveryGeneration.current;
  const beginIntentionalLogout = useCallback(() => {
    intentionalLogout.current.begin();
    setSessionError(null);
    return captureMessageCachePurge();
  }, []);

  const cancelIntentionalLogout = useCallback(() => {
    if (logoutAuthGeneration === authRecoveryGeneration.current) intentionalLogout.current.cancel();
  }, [logoutAuthGeneration]);

  const handleLogout = useCallback(() => {
    // Signing out is a member saying they are done on this machine, so the
    // unsent text they leave behind goes with the session rather than sitting
    // in storage for the retention window. Account-scoped keys already keep the
    // next member who signs in from ever reading it; this is what keeps it from
    // outliving the sitting it was typed in.
    if (currentUser) discardAccountDrafts(currentUser.id);
    if (logoutAuthGeneration === authRecoveryGeneration.current) clearAuthenticatedState();
  }, [currentUser, clearAuthenticatedState, logoutAuthGeneration]);

  if (!authChecked) {
    return (
      <div className="app-loading">
        <div className="loading-spinner-large" />
      </div>
    );
  }

  if (!authenticated) {
    if (showRegister) {
      return <RegisterPage onLogin={handleLogin} onBack={() => setShowRegister(false)} />;
    }
    return (
      <LoginPage
        onLogin={handleLogin}
        onRegister={() => setShowRegister(true)}
        errorMessage={sessionError}
      />
    );
  }

  if (!initialized) {
    return (
      <div className="app-loading">
        <div className="loading-spinner-large" />
        <p>
          <Trans>Loading…</Trans>
        </p>
      </div>
    );
  }

  if (serverFallbackGuildId && routeView.guildId !== serverFallbackGuildId) {
    return <Navigate to={routes.server(serverFallbackGuildId)} replace />;
  }

  const renderMain = () => {
    if (inaccessibleServer || (guildsLoaded && !routeGuildAccessible)) {
      return (
        <div className="no-channel" role="status">
          <p>
            <Trans>Project not found</Trans>
          </p>
        </div>
      );
    }
    if (
      routeView.guildId &&
      serverTransition.status === 'error' &&
      serverTransition.targetGuildId === routeView.guildId
    ) {
      return (
        <div className="no-channel" role="alert">
          <p>{i18n._(errorMessage(serverTransition.error, msg`Could not load this Project.`))}</p>
          <button type="button" className="no-channel-retry" onClick={retryServerTransition}>
            <Trans>Retry</Trans>
          </button>
        </div>
      );
    }
    if (
      serverTransitionPending ||
      (guildsLoaded && routeGuildAccessible && serverContextMismatch)
    ) {
      return (
        <div className="no-channel" role="status">
          <p>
            <Trans>Loading Project...</Trans>
          </p>
        </div>
      );
    }
    switch (routeView.view) {
      case 'overview':
        return (
          <PersonalOverview
            onOpenTaskThread={openOverviewTaskThread}
            onOpenAgentDm={openOverviewAgentPanel}
          />
        );
      case 'my-tasks':
        return (
          <PersonalTasksPage
            activeThreadId={myTasksThreadId}
            onTaskClick={(task) => {
              if (task.thread_id) openMyTaskThread(task.thread_id);
            }}
          />
        );
      case 'agents': {
        const selectedAgent = agentsList.find((a) => a.id === routeView.agentId);
        return routeView.agentId && selectedAgent ? (
          <AgentDetailView agentId={routeView.agentId} agent={selectedAgent} />
        ) : (
          <div className="no-channel">
            <p>
              <Trans>Select an agent from the sidebar</Trans>
            </p>
          </div>
        );
      }
      case 'tasks':
        return (
          <ServerTasksPage
            key={guildId ?? 'none'}
            guildId={routeView.guildId!}
            activeThreadId={serverTasksThreadId}
            onTaskClick={(task) => {
              if (task.thread_id) openServerTaskThread(task.thread_id);
            }}
          />
        );
      case 'insights':
        return (
          <InsightsPanel
            key={guildId ?? 'none'}
            guildId={routeView.guildId!}
            onTaskClick={(task) => {
              if (task.thread_id) openServerTaskThread(task.thread_id);
            }}
          />
        );
      case 'members':
        // Member selection remains route-addressable, but the Server workbench
        // owns its adjacent detail pane so management and DM actions stay in
        // the same Server context.
        return (
          <ProjectMembersPage
            selectedMemberId={routeView.memberId ?? null}
            onSelectMember={(id) => navigate(routes.member(guildId!, id))}
          />
        );
      case 'remote-nodes': {
        const computerId = routeView.computerId;
        if (computerId && computers.loading) {
          return (
            <div className="no-channel">
              <p>
                <Trans>Loading...</Trans>
              </p>
            </div>
          );
        }
        const selectedNode = computerId
          ? computers.nodes.find((n) => n.id === computerId)
          : undefined;
        return computerId && selectedNode ? (
          <ComputerDetailView
            nodeId={computerId}
            node={selectedNode}
            token={computers.connectionTokens[computerId]}
            onDeleted={() => {
              computers.forgetDeletedNode(computerId);
              navigate(routes.computers());
            }}
            onTokenRotated={(connectionToken) =>
              computers.recordConnectionToken(computerId, connectionToken)
            }
          />
        ) : (
          <div className="no-channel">
            <p>
              <Trans>Select a computer from the sidebar</Trans>
            </p>
          </div>
        );
      }
      case 'server-settings':
        return <ServerSettingsPage />;
      case 'invitations':
        return <InvitationsInbox />;
      case 'dm-list':
        return (
          <div className="no-channel">
            <p>
              <Trans>Select a conversation to start messaging</Trans>
            </p>
          </div>
        );
      case 'dm':
        if (routeView.dmChannelId) {
          return <ChannelView channelId={routeView.dmChannelId} />;
        }
        return null;
      case 'channel':
        if (routeView.channelId) {
          return (
            <ChannelView
              channelId={routeView.channelId}
              onThreadOpen={threadRoute.open}
              activeThreadMessageId={threadRoute.activeMessageId ?? undefined}
              onTaskClick={(task) => {
                if (task.thread_id) openServerTaskThread(task.thread_id);
              }}
            />
          );
        }
        return null;
      default:
        return (
          <div className="no-channel">
            <p>
              <Trans>Preparing your workspace. Refresh shortly…</Trans>
            </p>
            <button type="button" className="no-channel-retry" onClick={() => loadChannels()}>
              <Trans>Retry</Trans>
            </button>
          </div>
        );
    }
  };

  const renderSectionSidebar = () => {
    if (
      inaccessibleServer ||
      serverTransitionPending ||
      (guildsLoaded && !!routeView.guildId && (!routeGuildAccessible || serverContextMismatch))
    ) {
      return <div className="sidebar" aria-hidden="true" />;
    }
    switch (routeView.view) {
      case 'agents':
        return (
          <AgentSidebar
            selectedAgentId={routeView.agentId ?? null}
            onSelectAgent={(id) => navigate(routes.agent(id))}
          />
        );
      case 'remote-nodes':
        return (
          <ComputerSidebar
            nodes={computers.nodes}
            loading={computers.loading}
            loadError={computers.loadError}
            onlineById={computers.onlineById}
            selectedNodeId={routeView.computerId ?? null}
            onSelectNode={(id) => navigate(routes.computer(id))}
            onCreateNode={async (machineName) => {
              const node = await computers.createNode(machineName);
              navigate(routes.computer(node.id));
            }}
          />
        );
      case 'overview':
      case 'my-tasks':
        // Personal surfaces have no selected-Project navigation, so preserve
        // width for their content instead of rendering an inert empty column.
        return null;
      case 'dm':
      case 'dm-list':
        return <Sidebar />;
      case 'server-settings':
      case 'invitations':
        return <ServerSidebar />;
      default:
        return (
          <ServerSidebar
            onTaskClick={openServerTaskThread}
            activeThreadId={serverPanelThreadId}
            activeChannelId={routeView.view === 'channel' ? routeView.channelId : null}
          />
        );
    }
  };

  const renderSectionSidebarColumn = () => {
    const sidebar = renderSectionSidebar();
    return sidebar === null ? null : <div className="section-sidebar-column">{sidebar}</div>;
  };

  const renderThread = () => {
    if (routeView.view === 'overview' && overviewThreadId) {
      return <ThreadPanel threadId={overviewThreadId} onClose={closeOverviewPanel} />;
    }
    if (routeView.view === 'overview' && overviewAgentId) {
      const agent = agentsList.find((item) => item.id === overviewAgentId);
      return agent ? (
        <AgentDetailPanel agent={agent} onClose={closeOverviewPanel} />
      ) : (
        <div className="overview-panel-loading">
          <Trans>Loading Agent…</Trans>
        </div>
      );
    }
    if (routeView.view === 'overview' && overviewDmChannelId) {
      return <DirectMessagePanel channelId={overviewDmChannelId} onClose={closeOverviewPanel} />;
    }
    if (routeView.view === 'my-tasks' && myTasksThreadId) {
      return <ThreadPanel threadId={myTasksThreadId} onClose={closeMyTaskThread} />;
    }
    if (
      routeView.view === 'members' &&
      memberDmChannelId &&
      routeView.guildId &&
      routeView.memberId
    ) {
      const member = guildMembers.find((item) => item.user_id === routeView.memberId);
      return member ? (
        <DirectMessagePanel
          channelId={memberDmChannelId}
          onClose={() => navigate(routes.members(routeView.guildId!))}
        />
      ) : null;
    }
    if (!serverRouteReady) return null;
    if (serverPanelThreadId) {
      return <ThreadPanel threadId={serverPanelThreadId} onClose={closeServerTaskThread} />;
    }
    if (routeView.threadId) {
      return <ThreadPanel threadId={routeView.threadId} onClose={threadRoute.close} />;
    }
    if (threadRoute.pendingMessageId) {
      return (
        <ThreadPanel
          pendingMessageId={threadRoute.pendingMessageId}
          channelId={routeView.channelId!}
          onClose={threadRoute.close}
          onThreadCreated={threadRoute.created}
        />
      );
    }
    return null;
  };

  return (
    <div className="app">
      {oidcError && (
        <div className="oidc-global-error" role="alert" tabIndex={-1} ref={oidcErrorRef}>
          <span>{i18n._(oidcError)}</span>
          <button
            type="button"
            onClick={() => {
              setOIDCError(null);
              navigate(shellLocation.pathname, { replace: true });
            }}
          >
            <Trans>Dismiss</Trans>
          </button>
        </div>
      )}
      <ShellLayout
        isMobile={isMobile}
        primaryNavigation={
          <PrimaryNavigationRail
            onOpenSettings={openPersonalSettings}
            onLogout={handleLogout}
            onLogoutStart={beginIntentionalLogout}
            onLogoutFailure={cancelIntentionalLogout}
          />
        }
        sectionSidebar={renderSectionSidebarColumn()}
        main={renderMain()}
        thread={renderThread()}
        onThreadDismiss={
          overviewThreadId || overviewAgentId || overviewDmChannelId
            ? closeOverviewPanel
            : routeView.view === 'members' && memberDmChannelId
              ? () => navigate(routes.members(routeView.guildId!))
              : myTasksThreadId
                ? closeMyTaskThread
                : serverPanelThreadId
                  ? dismissServerTaskThread
                  : threadRoute.dismiss
        }
        sidebarCollapsed={sidebarCollapsed}
        onSidebarCollapsedToggle={toggleSidebarCollapsed}
        drawerOpen={drawerOpen}
        onDrawerToggle={toggleDrawer}
        onDrawerClose={closeDrawer}
      />
      <PersonalSettingsModal
        open={showPersonalSettings}
        onClose={() => setShowPersonalSettings(false)}
        restoreFocus={() => settingsRestoreFocus.current()}
      />
      {showCreateAgent && (
        <CreateAgentModal
          onClose={() => setShowCreateAgent(false)}
          onCreated={(agentId) => {
            setShowCreateAgent(false);
            if (agentId && guildId) navigate(routes.member(guildId, agentId));
          }}
        />
      )}
    </div>
  );
}

export function AppShell() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AntdProvider>
          <NavigationProvider>
            <AppInner />
          </NavigationProvider>
        </AntdProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default function App() {
  return <RouterProvider router={router} />;
}
